// salesProjectionService.ts
// Service to analyze past event sales patterns and project current event sales

interface Registration {
  completed_at: string;
  tickets_count: number;
}

interface PastEventData {
  id: string;
  title: string;
  start_date: string;
  end_date?: string;
  totalSold: number;
  registrations?: Registration[];
}

interface CurrentEventData {
  start_date: string;
  totalSold: number;
  registrations: Registration[];
  venueCapacity?: number;
  totalAvailable: number;
}

interface WeeklySalesPattern {
  weekNumber: number; // Negative numbers: -1 = final week, -2 = second to last, etc.
  averagePercentage: number;
  cumulativePercentage: number;
}

interface ProjectionResult {
  currentSold: number;
  projectedFinalSales: number;
  projectedCapacityPercentage: number;
  confidence: 'high' | 'medium' | 'low';
  weeksOnSale: number;
  totalWeeks: number;
  isOnTrack: boolean;
  daysUntilEvent: number;
}

/**
 * Analyzes past events to create a baseline weekly sales pattern
 * CALCULATES BACKWARDS FROM EVENT DATE to capture final week surge
 */
export const analyzePastEventsPattern = (pastEvents: PastEventData[]): WeeklySalesPattern[] => {
  const eventsWithPatterns: { weekNumber: number; cumulativePercentage: number }[][] = [];
  
  pastEvents.forEach(event => {
    // Skip events without registration data
    if (!event.registrations || event.registrations.length === 0 || event.totalSold === 0) return;
    
    const eventDate = new Date(event.start_date);
    
    // Find first and last sale dates
    const saleDates = event.registrations
      .map(reg => new Date(reg.completed_at).getTime())
      .sort((a, b) => a - b);
    
    if (saleDates.length === 0) return;
    
    const firstSaleDate = new Date(saleDates[0]);
    const lastSaleDate = new Date(saleDates[saleDates.length - 1]);
    
    // Calculate total weeks on sale
    const totalWeeks = Math.ceil(
      (eventDate.getTime() - firstSaleDate.getTime()) / (1000 * 60 * 60 * 24 * 7)
    );
    
    if (totalWeeks < 1) return; // Skip events with < 1 week sale period
    
    // Group sales by week - COUNTING BACKWARDS FROM EVENT DATE
    const salesByWeek: { [week: number]: number } = {};
    
    event.registrations.forEach(reg => {
      const regDate = new Date(reg.completed_at);
      
      // Calculate weeks BEFORE the event (negative numbers)
      const daysBeforeEvent = Math.floor(
        (eventDate.getTime() - regDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      
      // Convert to weeks before event (negative)
      // -1 = final week, -2 = second to last week, etc.
      const weeksBeforeEvent = -Math.ceil(daysBeforeEvent / 7);
      
      // Only count sales that happened before the event
      if (daysBeforeEvent >= 0) {
        salesByWeek[weeksBeforeEvent] = (salesByWeek[weeksBeforeEvent] || 0) + (reg.tickets_count || 1);
      }
    });
    
    // Convert to percentages - from earliest week to final week
    const weekNumbers = Object.keys(salesByWeek).map(Number).sort((a, b) => a - b);
    
    if (weekNumbers.length === 0) return;
    
    // First calculate cumulative for THIS event
    const eventCumulativePattern: { weekNumber: number; cumulativePercentage: number }[] = [];
    let cumulative = 0;
    
    weekNumbers.forEach(weekNum => {
      const soldThisWeek = salesByWeek[weekNum] || 0;
      const weeklyPercentage = (soldThisWeek / event.totalSold) * 100;
      cumulative += weeklyPercentage;
      
      eventCumulativePattern.push({ 
        weekNumber: weekNum, 
        cumulativePercentage: cumulative 
      });
    });
    
    eventsWithPatterns.push(eventCumulativePattern);
  });
  
  if (eventsWithPatterns.length === 0) {
    return [];
  }
  
  // Find the range of weeks we need to cover
  let minWeek = 0;
  let maxWeek = -1;
  
  eventsWithPatterns.forEach(pattern => {
    pattern.forEach(week => {
      minWeek = Math.min(minWeek, week.weekNumber);
      maxWeek = Math.max(maxWeek, week.weekNumber);
    });
  });
  
  // Calculate average pattern across all events - from earliest to final week
  const baselinePattern: WeeklySalesPattern[] = [];
  
  // Build pattern from earliest week to final week (-1)
  for (let week = minWeek; week <= maxWeek; week++) {
    // Get cumulative percentages at this week from all events that have data
    const cumulativeAtThisWeek = eventsWithPatterns
      .map(pattern => {
        // Find this exact week or the latest week before it
        const relevantWeeks = pattern.filter(p => p.weekNumber <= week);
        if (relevantWeeks.length === 0) return null;
        // Get the latest cumulative percentage at or before this week
        return relevantWeeks[relevantWeeks.length - 1].cumulativePercentage;
      })
      .filter(p => p !== null) as number[];
    
    const avgCumulative = cumulativeAtThisWeek.length > 0
      ? cumulativeAtThisWeek.reduce((sum, p) => sum + p, 0) / cumulativeAtThisWeek.length
      : 0;
    
    // Calculate average percentage for THIS week (difference from previous week)
    const prevWeekCumulative = week > minWeek 
      ? baselinePattern[baselinePattern.length - 1]?.cumulativePercentage || 0
      : 0;
    
    const averagePercentage = avgCumulative - prevWeekCumulative;
    
    baselinePattern.push({
      weekNumber: week,
      averagePercentage,
      cumulativePercentage: avgCumulative
    });
  }
  
  // Ensure final cumulative is capped at 100%
  const finalCumulative = baselinePattern[baselinePattern.length - 1]?.cumulativePercentage || 100;
  if (finalCumulative > 100) {
    const normalizationFactor = 100 / finalCumulative;
    baselinePattern.forEach(pattern => {
      pattern.averagePercentage *= normalizationFactor;
      pattern.cumulativePercentage *= normalizationFactor;
    });
    console.warn('⚠️ Baseline pattern cumulative > 100%, normalized from', finalCumulative, 'to 100%');
  }
  
  console.log('📊 Baseline pattern created:', {
    totalEvents: eventsWithPatterns.length,
    weekRange: `${minWeek} to ${maxWeek}`,
    patterns: baselinePattern.map(p => ({
      week: p.weekNumber,
      avgPct: p.averagePercentage.toFixed(1),
      cumPct: p.cumulativePercentage.toFixed(1)
    }))
  });
  
  return baselinePattern;
};

/**
 * Projects final sales for a current event based on baseline pattern
 */
export const projectEventSales = (
  currentEvent: CurrentEventData,
  baselinePattern: WeeklySalesPattern[]
): ProjectionResult => {
  if (currentEvent.registrations.length === 0) {
    return {
      currentSold: 0,
      projectedFinalSales: 0,
      projectedCapacityPercentage: 0,
      confidence: 'low',
      weeksOnSale: 0,
      totalWeeks: 0,
      isOnTrack: false,
      daysUntilEvent: Math.ceil(
        (new Date(currentEvent.start_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
      )
    };
  }
  
  const eventDate = new Date(currentEvent.start_date);
  const now = new Date();
  
  // Calculate how long event has been on sale
  const saleDates = currentEvent.registrations
    .map(reg => new Date(reg.completed_at).getTime())
    .sort((a, b) => a - b);
  
  const firstSaleDate = new Date(saleDates[0]);
  
  // Calculate total weeks until event and current weeks before event
  const daysUntilEvent = Math.ceil(
    (eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
  );
  
  const weeksUntilEvent = Math.ceil(daysUntilEvent / 7);
  const currentWeeksBeforeEvent = -weeksUntilEvent; // Negative number
  
  const totalWeeks = Math.ceil(
    (eventDate.getTime() - firstSaleDate.getTime()) / (1000 * 60 * 60 * 24 * 7)
  );
  
  const weeksOnSale = Math.floor(
    (now.getTime() - firstSaleDate.getTime()) / (1000 * 60 * 60 * 24 * 7)
  );
  
  // Find the expected cumulative percentage at this point (weeks before event)
  const expectedPattern = baselinePattern.find(p => p.weekNumber === currentWeeksBeforeEvent);
  
  console.log('🔍 PROJECTION DEBUG:', {
    currentSold: currentEvent.totalSold,
    daysUntilEvent,
    weeksUntilEvent,
    currentWeeksBeforeEvent,
    totalWeeks,
    weeksOnSale,
    expectedPattern: expectedPattern ? {
      weekNumber: expectedPattern.weekNumber,
      cumulativePercentage: expectedPattern.cumulativePercentage
    } : 'NOT FOUND',
    baselinePatternLength: baselinePattern.length,
    baselinePatternRange: baselinePattern.length > 0 ? {
      min: baselinePattern[0].weekNumber,
      max: baselinePattern[baselinePattern.length - 1].weekNumber
    } : 'EMPTY'
  });
  
  if (!expectedPattern) {
    // No pattern data for this specific week - use interpolation or simple projection
    // Find closest pattern data points
    const closestBefore = baselinePattern
      .filter(p => p.weekNumber < currentWeeksBeforeEvent)
      .sort((a, b) => b.weekNumber - a.weekNumber)[0];
    
    const closestAfter = baselinePattern
      .filter(p => p.weekNumber > currentWeeksBeforeEvent)
      .sort((a, b) => a.weekNumber - b.weekNumber)[0];
    
    let estimatedPercentage = 0;
    
    if (closestBefore && closestAfter) {
      // Interpolate between the two
      const weekDiff = closestAfter.weekNumber - closestBefore.weekNumber;
      const percentDiff = closestAfter.cumulativePercentage - closestBefore.cumulativePercentage;
      const weekOffset = currentWeeksBeforeEvent - closestBefore.weekNumber;
      estimatedPercentage = closestBefore.cumulativePercentage + (percentDiff * weekOffset / weekDiff);
    } else if (closestBefore) {
      estimatedPercentage = closestBefore.cumulativePercentage;
    } else if (closestAfter) {
      estimatedPercentage = closestAfter.cumulativePercentage * 0.5; // Assume halfway
    } else {
      // Fallback to simple linear projection
      const weeksRemaining = totalWeeks - weeksOnSale;
      const projectedFinalSales = weeksRemaining > 0
        ? currentEvent.totalSold * (totalWeeks / weeksOnSale)
        : currentEvent.totalSold;
      
      const capacity = currentEvent.venueCapacity || currentEvent.totalAvailable;
      
      return {
        currentSold: currentEvent.totalSold,
        projectedFinalSales: Math.round(projectedFinalSales),
        projectedCapacityPercentage: capacity > 0 ? Math.round((projectedFinalSales / capacity) * 1000) / 10 : 0,
        confidence: 'low',
        weeksOnSale,
        totalWeeks,
        isOnTrack: false,
        daysUntilEvent
      };
    }
    
    if (estimatedPercentage === 0 || estimatedPercentage > 100) {
      estimatedPercentage = Math.max(1, Math.min(99, (weeksOnSale / totalWeeks) * 100));
    }
    
    const projectedFinalSales = (currentEvent.totalSold / estimatedPercentage) * 100;
    const capacity = currentEvent.venueCapacity || currentEvent.totalAvailable;
    const projectedCapacityPercentage = capacity > 0 ? (projectedFinalSales / capacity) * 100 : 0;
    
    return {
      currentSold: currentEvent.totalSold,
      projectedFinalSales: Math.round(projectedFinalSales),
      projectedCapacityPercentage: Math.round(projectedCapacityPercentage * 10) / 10,
      confidence: 'medium',
      weeksOnSale,
      totalWeeks,
      isOnTrack: projectedCapacityPercentage >= 80,
      daysUntilEvent
    };
  }
  
  // Project final sales based on baseline pattern
  const currentPercentageOfFinal = expectedPattern.cumulativePercentage;
  
  if (currentPercentageOfFinal === 0 || currentPercentageOfFinal > 100) {
    return {
      currentSold: currentEvent.totalSold,
      projectedFinalSales: currentEvent.totalSold,
      projectedCapacityPercentage: 0,
      confidence: 'low',
      weeksOnSale,
      totalWeeks,
      isOnTrack: false,
      daysUntilEvent
    };
  }
  
  const projectedFinalSales = (currentEvent.totalSold / currentPercentageOfFinal) * 100;
  
  const capacity = currentEvent.venueCapacity || currentEvent.totalAvailable;
  const projectedCapacityPercentage = capacity > 0 ? (projectedFinalSales / capacity) * 100 : 0;
  
  // Determine confidence based on how many weeks we have data for
  let confidence: 'high' | 'medium' | 'low' = 'low';
  if (weeksOnSale >= 3) confidence = 'high';
  else if (weeksOnSale >= 2) confidence = 'medium';
  
  // Determine if on track (projected to reach at least 80% capacity)
  const isOnTrack = projectedCapacityPercentage >= 80;
  
  return {
    currentSold: currentEvent.totalSold,
    projectedFinalSales: Math.round(projectedFinalSales),
    projectedCapacityPercentage: Math.round(projectedCapacityPercentage * 10) / 10,
    confidence,
    weeksOnSale,
    totalWeeks,
    isOnTrack,
    daysUntilEvent
  };
};

/**
 * Get a recommendation based on projection
 */
export const getProjectionRecommendation = (projection: ProjectionResult): string => {
  const { projectedCapacityPercentage, isOnTrack, daysUntilEvent, confidence } = projection;
  
  if (confidence === 'low') {
    return "Not enough sales data yet to make accurate projections. Monitor closely over the next week.";
  }
  
  if (projectedCapacityPercentage >= 100) {
    return `🎉 Event is projected to SELL OUT! Consider adding a second show or waitlist with ${daysUntilEvent} days remaining.`;
  }
  
  if (projectedCapacityPercentage >= 90) {
    return "Event is tracking very well and likely to sell out. Keep momentum going!";
  }
  
  if (projectedCapacityPercentage >= 80) {
    return "Event is on track to reach target capacity. Continue current marketing efforts.";
  }
  
  if (projectedCapacityPercentage >= 60) {
    return "Event is underperforming. Consider targeted marketing push or promotional offers.";
  }
  
  if (projectedCapacityPercentage >= 40) {
    return "⚠️ Event needs immediate attention. Urgent marketing intervention required.";
  }
  
  return "🚨 Event is severely underperforming. Consider rescheduling or major promotional campaign.";
};

export default {
  analyzePastEventsPattern,
  projectEventSales,
  getProjectionRecommendation
};